<!--
  Tracciability filter modal — redesigned 2026-06-09 (v2.5.0).

  Cascade filter modal for traceability search. Replaces the legacy
  kendo-grid layout with column-header-embedded dropdown buttons
  (hard to discover) with explicit dropdown chip-buttons in a row
  ABOVE the results table — the cascade order (articolo → fornitore
  → lotto → seriale) is now visually obvious thanks to enabled/
  disabled state styling.

  Modal pattern Diakonix-coherent: fixed backdrop, centered card,
  100vw/100dvh dimensions for containing-block insulation.
-->

@if (open) {

  <!-- Click on backdrop closes the modal (event bubbling) -->
  <div class="modal-backdrop" (click)="closeModal.emit()">
    <aside class="modal" (click)="$event.stopPropagation(); closeDropdowns()">

      <!-- ─── Header ─────────────────────────────────────────────── -->
      <header class="modal__header">
        <h2 class="modal__title">🎯 Tracciabilità articoli</h2>
        <button type="button" class="modal-close" (click)="closeModal.emit()" aria-label="Chiudi">×</button>
      </header>

      <!-- ─── Body ───────────────────────────────────────────────── -->
      <div class="modal__body">

        <!-- Cascade dropdown row (4 filters: art → for → lot → ser) -->
        <div class="cascade-row" role="group" aria-label="Filtri cascade">
          <button
            type="button"
            class="cascade-btn"
            [class.cascade-btn--active]="isActive('product_code')"
            [class.cascade-btn--open]="activeDropdown === 'product_code'"
            (click)="toggleDropdown('product_code', $event)"
            [attr.aria-expanded]="activeDropdown === 'product_code'"
          >
            <span class="cascade-btn__step">1</span>
            <span class="cascade-btn__text">
              <span class="cascade-btn__label">Articolo</span>
              <span class="cascade-btn__value">
                {{ selectedArt || 'Seleziona' }}
              </span>
            </span>
            <span class="cascade-btn__chevron" aria-hidden="true">▾</span>
          </button>

          <span class="cascade-arrow" aria-hidden="true">→</span>

          <button
            type="button"
            class="cascade-btn"
            [class.cascade-btn--active]="isActive('order_number')"
            [class.cascade-btn--disabled]="isDisabled('order_number')"
            [class.cascade-btn--open]="activeDropdown === 'order_number'"
            (click)="toggleDropdown('order_number', $event)"
            [attr.aria-expanded]="activeDropdown === 'order_number'"
            [title]="isDisabled('order_number') ? 'Seleziona prima un articolo' : 'Filtra per codice fornitore'"
          >
            <span class="cascade-btn__step">2</span>
            <span class="cascade-btn__text">
              <span class="cascade-btn__label">Cod. fornitore</span>
              <span class="cascade-btn__value">
                {{ selectedFor || 'Seleziona' }}
              </span>
            </span>
            <span class="cascade-btn__chevron" aria-hidden="true">▾</span>
          </button>

          <span class="cascade-arrow" aria-hidden="true">→</span>

          <button
            type="button"
            class="cascade-btn"
            [class.cascade-btn--active]="isActive('lotto')"
            [class.cascade-btn--disabled]="isDisabled('lotto')"
            [class.cascade-btn--open]="activeDropdown === 'lotto'"
            (click)="toggleDropdown('lotto', $event)"
            [attr.aria-expanded]="activeDropdown === 'lotto'"
            [title]="isDisabled('lotto') ? 'Seleziona prima un fornitore' : 'Filtra per lotto'"
          >
            <span class="cascade-btn__step">3</span>
            <span class="cascade-btn__text">
              <span class="cascade-btn__label">Lotto</span>
              <span class="cascade-btn__value">
                {{ selectedLot ? lotLabel(selectedLot) : 'Seleziona' }}
              </span>
            </span>
            <span class="cascade-btn__chevron" aria-hidden="true">▾</span>
          </button>

          <span class="cascade-arrow" aria-hidden="true">→</span>

          <button
            type="button"
            class="cascade-btn"
            [class.cascade-btn--active]="isActive('serial')"
            [class.cascade-btn--disabled]="isDisabled('serial')"
            [class.cascade-btn--open]="activeDropdown === 'serial'"
            (click)="toggleDropdown('serial', $event)"
            [attr.aria-expanded]="activeDropdown === 'serial'"
            [title]="isDisabled('serial') ? 'Seleziona prima un lotto' : 'Filtra per seriale'"
          >
            <span class="cascade-btn__step">4</span>
            <span class="cascade-btn__text">
              <span class="cascade-btn__label">N° seriale</span>
              <span class="cascade-btn__value">
                {{ selectedSerial ? selectedSerial.from + '–' + selectedSerial.to : 'Seleziona' }}
              </span>
            </span>
            <span class="cascade-btn__chevron" aria-hidden="true">▾</span>
          </button>
        </div>

        <!-- Active filter chips (compact summary) -->
        @if (hasAnyFilter()) {
          <div class="filter-chips">
            <span class="filter-chips__label">Filtri attivi:</span>
            @if (selectedArt) {
              <span class="filter-chip">
                📦 {{ selectedArt }}
                <button type="button" class="filter-chip__remove"
                        (click)="selectArt(selectedArt); $event.stopPropagation()"
                        aria-label="Rimuovi articolo">×</button>
              </span>
            }
            @if (selectedFor) {
              <span class="filter-chip">
                🏭 {{ selectedFor }}
                <button type="button" class="filter-chip__remove"
                        (click)="selectFor(selectedFor); $event.stopPropagation()"
                        aria-label="Rimuovi fornitore">×</button>
              </span>
            }
            @if (selectedLot) {
              <span class="filter-chip">
                📅 {{ lotLabel(selectedLot) }}
                <button type="button" class="filter-chip__remove"
                        (click)="selectLot(selectedLot); $event.stopPropagation()"
                        aria-label="Rimuovi lotto">×</button>
              </span>
            }
            @if (selectedSerial) {
              <span class="filter-chip">
                🔢 {{ selectedSerial.from }}–{{ selectedSerial.to }}
                <button type="button" class="filter-chip__remove"
                        (click)="selectSerial(selectedSerial); $event.stopPropagation()"
                        aria-label="Rimuovi seriale">×</button>
              </span>
            }
            <button type="button" class="filter-chips__clear"
                    (click)="clearAll(); $event.stopPropagation()">
              Rimuovi tutti
            </button>
          </div>
        }

        <!-- Results table OR loading OR empty hint -->
        <section class="results-area" aria-label="Risultati ricerca">
          @if (loadingResults) {
            <div class="results-loading">
              <div class="results-spinner"></div>
              <p class="results-loading__msg">Ricerca in corso…</p>
            </div>
          } @else if (!hasAnyFilter()) {
            <div class="results-hint">
              <div class="results-hint__icon" aria-hidden="true">👆</div>
              <p class="results-hint__msg">
                Inizia selezionando un <strong>articolo</strong> dal primo dropdown.
                I filtri successivi si abiliteranno via via.
              </p>
            </div>
          } @else if (filteredResults.length === 0) {
            <div class="results-empty">
              <div class="results-empty__icon" aria-hidden="true">📭</div>
              <p class="results-empty__msg">Nessun articolo corrisponde ai filtri selezionati.</p>
            </div>
          } @else {
            <div class="results-table-scroll">
              <table class="results-table" role="table">
                <thead>
                  <tr>
                    <th class="col-article">Articolo</th>
                    <th class="col-order">Codice fornitore</th>
                    <th class="col-lot">Lotto</th>
                    <th class="col-serial">N° seriale</th>
                    <th class="col-action" aria-label="Apri"></th>
                  </tr>
                </thead>
                <tbody>
                  @for (r of filteredResults; track r.product_qr + r.serial) {
                    <tr
                      class="results-row"
                      (click)="openDetail.emit(r); $event.stopPropagation()"
                      title="Apri tracking 5-stage"
                    >
                      <td class="col-article">
                        <span class="cell-article">{{ r.product_code || '—' }}</span>
                      </td>
                      <td class="col-order">{{ r.order_number || '—' }}</td>
                      <td class="col-lot">{{ formatLot(r.batch_year, r.batch_week) }}</td>
                      <td class="col-serial">{{ r.serial || '—' }}</td>
                      <td class="col-action">
                        <span class="results-row__chevron" aria-hidden="true">›</span>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
        </section>

      </div>

      <!-- ─── Footer ─────────────────────────────────────────────── -->
      <footer class="modal__footer">
        @if (hasAnyFilter()) {
          <button type="button" class="btn btn--ghost" (click)="clearAll(); $event.stopPropagation()">
            Pulisci filtri
          </button>
        }
        <button type="button" class="btn btn--primary" (click)="closeModal.emit()">
          Chiudi
        </button>
      </footer>

    </aside>

    <!-- ── Floating dropdown panel ──────────────────────────────────
         Rendered as a sibling of the modal card so it can escape the
         modal's overflow:hidden when the dropdown opens near an edge.
         Position is computed in TS via `panelPos`. -->
    @if (activeDropdown) {
      <div
        class="cascade-panel"
        [style.top.px]="panelPos.top"
        [style.left.px]="panelPos.left"
        (click)="$event.stopPropagation()"
      >
        <header class="cascade-panel__header">
          <span class="cascade-panel__title">
            {{ panelMeta().title }}
          </span>
        </header>

        <div class="cascade-panel__body">
          @if (activeDropdown === 'product_code') {
            @if (loadingProducts) {
              <div class="cascade-panel__loading">
                <div class="results-spinner"></div>
                <span>Caricamento…</span>
              </div>
            } @else if (products.length === 0) {
              <p class="cascade-panel__empty">Nessun articolo disponibile.</p>
            } @else {
              @for (item of products; track item.pr_codice; let i = $index) {
                <button
                  type="button"
                  class="cascade-item"
                  [class.cascade-item--selected]="selectedArt === item.pr_codice"
                  (click)="$event.stopPropagation(); selectArt(item.pr_codice)"
                >
                  <span class="cascade-item__num">{{ i + 1 }}</span>
                  <span class="cascade-item__label">{{ item.pr_codice }}</span>
                  <span class="cascade-item__check" aria-hidden="true"
                        [style.visibility]="selectedArt === item.pr_codice ? 'visible' : 'hidden'">✓</span>
                </button>
              }
            }
          }

          @if (activeDropdown === 'order_number') {
            @if (loadingSuppliers) {
              <div class="cascade-panel__loading">
                <div class="results-spinner"></div>
                <span>Caricamento…</span>
              </div>
            } @else if (suppliers.length === 0) {
              <p class="cascade-panel__empty">Nessun fornitore disponibile.</p>
            } @else {
              @for (item of suppliers; track item.codice_fornitore; let i = $index) {
                <button
                  type="button"
                  class="cascade-item"
                  [class.cascade-item--selected]="selectedFor === item.codice_fornitore"
                  (click)="$event.stopPropagation(); selectFor(item.codice_fornitore)"
                >
                  <span class="cascade-item__num">{{ i + 1 }}</span>
                  <span class="cascade-item__label">{{ item.codice_fornitore }}</span>
                  <span class="cascade-item__check" aria-hidden="true"
                        [style.visibility]="selectedFor === item.codice_fornitore ? 'visible' : 'hidden'">✓</span>
                </button>
              }
            }
          }

          @if (activeDropdown === 'lotto') {
            @if (loadingLots) {
              <div class="cascade-panel__loading">
                <div class="results-spinner"></div>
                <span>Caricamento…</span>
              </div>
            } @else if (lots.length === 0) {
              <p class="cascade-panel__empty">Nessun lotto disponibile.</p>
            } @else {
              @for (item of lots; track item.lot; let i = $index) {
                <button
                  type="button"
                  class="cascade-item"
                  [class.cascade-item--selected]="selectedLot?.lot === item.lot"
                  (click)="$event.stopPropagation(); selectLot(item)"
                >
                  <span class="cascade-item__num">{{ i + 1 }}</span>
                  <span class="cascade-item__label">{{ lotLabel(item) }}</span>
                  <span class="cascade-item__check" aria-hidden="true"
                        [style.visibility]="selectedLot?.lot === item.lot ? 'visible' : 'hidden'">✓</span>
                </button>
              }
            }
          }

          @if (activeDropdown === 'serial') {
            @if (loadingSerials) {
              <div class="cascade-panel__loading">
                <div class="results-spinner"></div>
                <span>Caricamento…</span>
              </div>
            } @else if (serials.length === 0) {
              <p class="cascade-panel__empty">Nessun seriale disponibile.</p>
            } @else {
              @for (item of serials; track item.key; let i = $index) {
                <button
                  type="button"
                  class="cascade-item"
                  [class.cascade-item--selected]="selectedSerial?.key === item.key"
                  (click)="$event.stopPropagation(); selectSerial(item)"
                >
                  <span class="cascade-item__num">{{ i + 1 }}</span>
                  <span class="cascade-item__label">{{ item.from }}–{{ item.to }}</span>
                  <span class="cascade-item__meta">{{ item.qty }} pz</span>
                  <span class="cascade-item__check" aria-hidden="true"
                        [style.visibility]="selectedSerial?.key === item.key ? 'visible' : 'hidden'">✓</span>
                </button>
              }
            }
          }
        </div>
      </div>
    }

  </div>
}
